import { Check, FilterAlt } from "@mui/icons-material";
import { Box, Button, Container, Divider, FormControl, FormControlLabel, FormLabel, MenuItem, Popover, Radio, RadioGroup, Select, TextField, TextFieldProps, Typography } from "@mui/material";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { SharedItemRequestModel } from "../../../model/SharedItemRequestModel";
import { UserMinimal } from "../../../model/UserModel";
import { useProfile } from "../../../context/ProfileProvider/useProfile";
import DatePickerWithLocale from "../../../components/KeydriveComponents/DatePicker/DatePickerWithLocale";
import { useParams } from "react-router-dom";

type ButtonWithPopoverFilterProps = {
    disabledButton?: boolean;
    users: UserMinimal[];
    onApplyFilter: (data: SharedItemRequestModel) => void;
};

export default function ButtonWithPopoverFilter({
    disabledButton = false,
    users,
    onApplyFilter
}: ButtonWithPopoverFilterProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const { t } = useTranslation();
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const [filterType, setFilterType] = useState<string>('all');
    const [selectedUser, setSelectedUser] = useState<string | null>('');
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const profile = useProfile();
    const { nameOrg } = useParams();
    const [dataFilter, setDataFilter] = useState<SharedItemRequestModel>({nameOrgInternal: nameOrg!,});
    
    const isApplyDisabled = !(
        filterType === 'all' ||
        (filterType === 'mine' && (selectedUser == null || selectedUser == '') && startDate == null && endDate == null) ||
        (filterType === 'user' && selectedUser) ||
        (filterType === 'date' && startDate && endDate)
    );

    const resetFilters = () => {
        setFilterType('all');
        setSelectedUser('');
        setStartDate(null);
        setEndDate(null);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const applyFilters = () => {
        let data: SharedItemRequestModel = {nameOrgInternal: nameOrg!,};
        if (filterType === 'all') {
            resetFilters();
        } else if (filterType === 'mine') {
            data = {...data,
                myFiles: true,
            };
        } else if (filterType === 'user') {
            data = {...data,
                userSharedOwnerId: selectedUser,
            };
        } else if (filterType === 'date') {
            data = {...data,
                startDate: startDate ? dayjs(startDate, 'DD/MM/YYYY').toString() : undefined,
                endDate: endDate ? dayjs(endDate, 'DD/MM/YYYY').toString() : undefined,
            };
        }
        onApplyFilter(data);
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    useEffect(() => {
        if (filterType === 'all') {
            resetFilters();
        } else if (filterType === 'mine') {
            setSelectedUser('');
            setStartDate(null);
            setEndDate(null);
            setDataFilter({
                ...dataFilter,
                myFiles: true
            });
        } else if (filterType === 'user') {
            setStartDate(null);
            setEndDate(null);
            setDataFilter({
                ...dataFilter,
                userSharedOwnerId: selectedUser,
            });
        } else if (filterType === 'date') {
            setSelectedUser('');
            setDataFilter({
                ...dataFilter,
                startDate: startDate ? dayjs(startDate).toString() : undefined,
                endDate: endDate ? dayjs(endDate).toString() : undefined,
            });
        }
    }, [filterType])
    return (
        <div>
            <Button
                disabled={disabledButton}
                variant="contained"
                startIcon={<FilterAlt />}
                sx={{
                    height: '36px',
                    backgroundColor: '#FFFFFF',
                    color: '#22638D',
                    border: '1px solid #22638D',
                    textTransform: 'none',
                    font: 'normal normal normal 14px/21px Segoe UI',
                    whiteSpace: 'nowrap',
                    "&:hover": {
                        backgroundColor: '#22638D',
                        color: '#FFFFFF',
                    }
                }}
                aria-describedby={id}
                onClick={handleClick}
            >
                {t('Filters.message')}
            </Button>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                sx={{
                    marginTop: "10px",
                    boxShadow: "3px 3px 6px #22638D33"
                }}
            >
                <Container sx={{
                    padding: "15px 0 !important",
                    width: "332px",
                    height: "473px",
                    border: '1px solid #22638D',
                    borderRadius: "6px"
                }}>



                    <Box >
                        <Typography sx={{
                            font: "normal normal 600 18px/27px Segoe UI",
                            color: "#2D2D2D",
                            paddingLeft: "15px"
                        }}>{t("ButtonWithPopoverFilter.filters")}</Typography>
                        <Divider />
                        <FormControl sx={{ width: "100%" }} component="fieldset">
                            <RadioGroup
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                            >
                                <FormControlLabel
                                    sx={{
                                        font: "normal normal normal 16px/24px Segoe UI",
                                        color: "#2D2D2D",
                                        paddingLeft: "15px",
                                        "&.MuiFormControlLabel-label": {
                                            font: "normal normal normal 11px/24px Segoe UI !important",
                                        }
                                    }}
                                    value="all"
                                    control={
                                        <Radio
                                            size="small"
                                            sx={{
                                                color: "#22638D",
                                                '&.Mui-checked': { color: "#22638D" },
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: 16,
                                                },
                                            }} />
                                    }
                                    label={
                                        <Typography
                                            sx={{
                                                font: "normal normal normal 16px/24px Segoe UI",
                                                color: "#2D2D2D",
                                            }}>
                                            {t("ButtonWithPopoverFilter.viewAll")}
                                        </Typography>
                                    } />
                                <Divider />
                                <FormControlLabel
                                    sx={{
                                        font: "normal normal normal 16px/24px Segoe UI",
                                        color: "#2D2D2D",
                                        paddingLeft: "15px"
                                    }}
                                    value="mine"
                                    control={
                                        <Radio
                                            sx={{
                                                color: "#22638D",
                                                '&.Mui-checked': { color: "#22638D" },
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: 16,
                                                },
                                            }} />
                                    }
                                    label={
                                        <Typography
                                            sx={{
                                                font: "normal normal normal 16px/24px Segoe UI",
                                                color: "#2D2D2D",
                                            }}>
                                            {t("ButtonWithPopoverFilter.myFiles")}
                                        </Typography>
                                    }
                                />
                                <Divider />
                                <FormControlLabel
                                    sx={{
                                        font: "normal normal normal 16px/24px Segoe UI",
                                        color: "#2D2D2D",
                                        paddingLeft: "15px"
                                    }}
                                    value="user"
                                    control={
                                        <Radio
                                            sx={{
                                                color: "#22638D",
                                                '&.Mui-checked': { color: "#22638D" },
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: 16,
                                                },
                                            }} />
                                    }
                                    label={
                                        <Typography
                                            sx={{
                                                font: "normal normal normal 16px/24px Segoe UI",
                                                color: "#2D2D2D",
                                            }}>
                                            {t("ButtonWithPopoverFilter.sharedBy")}
                                        </Typography>
                                    }
                                />
                                <Box mt={2}>
                                    <FormControl variant="standard" fullWidth sx={{ paddingLeft: "15px", paddingRight: "15px" }}>
                                        <Select
                                        disabled={filterType != "user"}
                                            sx={{ paddingLeft: "15px" }}
                                            value={selectedUser}
                                            onChange={(e) => setSelectedUser(e.target.value)}
                                            displayEmpty
                                            size="small"
                                        >
                                            {users.map(u => <MenuItem key={u.id} value={u.id}>{u.email}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Divider sx={{ marginTop: "10px" }} />
                                <FormControlLabel
                                    sx={{
                                        font: "normal normal normal 16px/24px Segoe UI",
                                        color: "#2D2D2D",
                                        paddingLeft: "15px"
                                    }}
                                    value="date"
                                    control={
                                        <Radio
                                            sx={{
                                                color: "#22638D",
                                                '&.Mui-checked': { color: "#22638D" },
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: 16,
                                                },
                                            }} />
                                    }
                                    label={
                                        <Typography
                                            sx={{
                                                font: "normal normal normal 16px/24px Segoe UI",
                                                color: "#2D2D2D",
                                            }}>
                                            {t("ButtonWithPopoverFilter.sharedDate")}
                                        </Typography>
                                    }
                                />
                                <Box>
                                    <DatePickerWithLocale
                                        disabled={filterType != "date"}
                                        label={t("ButtonWithPopoverFilter.start")}
                                        value={startDate}
                                        onChange={(newValue) => {
                                            setStartDate(newValue);
                                        }}
                                        renderInput={(params: any) => (
                                            <TextField
                                                {...params}
                                                variant="standard"
                                                fullWidth
                                                inputProps={{
                                                    ...params.inputProps,
                                                    placeholder: t("ButtonWithPopoverFilter.start"),
                                                    autoComplete: "off"
                                                }}
                                                sx={{
                                                    paddingLeft: "15px",
                                                    paddingRight: "15px",
                                                    '& label': {
                                                        paddingLeft: "15px",
                                                        paddingRight: "15px",
                                                    },
                                                    '& label.Mui-focused': {
                                                        paddingLeft: "15px",
                                                        paddingRight: "15px",
                                                    },
                                                }}
                                            />)}
                                    />
                                    <Box mt={2}>
                                        <DatePickerWithLocale
                                            disabled={filterType != "date"}
                                            label={t("ButtonWithPopoverFilter.end")}
                                            value={endDate}
                                            onChange={(newValue) => {
                                                setEndDate(newValue);
                                            }}
                                            renderInput={(params: any) => (
                                                <TextField
                                                    {...params}
                                                    variant="standard"
                                                    fullWidth
                                                    inputProps={{
                                                        ...params.inputProps,
                                                        placeholder: t("ButtonWithPopoverFilter.end"),
                                                        autoComplete: "off"
                                                    }}
                                                    sx={{
                                                        paddingLeft: "15px",
                                                        paddingRight: "15px",
                                                        '& label': {
                                                            paddingLeft: "15px",
                                                            paddingRight: "15px",
                                                        },
                                                        '& label.Mui-focused': {
                                                            paddingLeft: "15px",
                                                            paddingRight: "15px",
                                                        },
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </Box>
                            </RadioGroup>
                        </FormControl>




                        <Divider sx={{ my: 2 }} />

                        <Box display="flex" justifyContent="space-between" sx={{ padding: "0 15px" }}>
                            <Button
                                onClick={resetFilters}
                                variant="outlined"
                                sx={{
                                    height: '36px',
                                    backgroundColor: '#FFFFFF',
                                    color: '#22638D',
                                    border: '1px solid #22638D',
                                    textTransform: 'none',
                                    font: 'normal normal normal 14px/21px Segoe UI',
                                    whiteSpace: 'nowrap',
                                    "&:hover": {
                                        backgroundColor: '#22638D',
                                        color: '#FFFFFF',
                                    }
                                }}
                            >
                                {t("ButtonWithPopoverFilter.reset")}
                            </Button>
                            <Button
                                onClick={applyFilters}
                                variant="contained"
                                disabled={isApplyDisabled}
                                startIcon={<Check />}
                                sx={{
                                    height: '36px',
                                    backgroundColor: '#22638D',
                                    color: '#FFFFFF',
                                    border: '1px solid #22638D',
                                    textTransform: 'none',
                                    font: 'normal normal normal 14px/21px Segoe UI',
                                    whiteSpace: 'nowrap',
                                    "&:hover": {
                                        backgroundColor: '#FFFFFF',
                                        color: '#22638D',
                                    }
                                }}
                            >
                                {t("ButtonWithPopoverFilter.applyFilter")}
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </Popover>
        </div>
    );
}